<template>
  <div>
    <SectionHeader :title="$t('meta.costCenters')">
      <template v-slot:meta>
        <Link
          :to="`/company/cost_centers/new`"
          class="button button-green button-round button-small"
          style="display: inline-block; position: relative; top: -3px"
          >{{ $t('costCenters.addCostCenter') }}</Link
        >
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />

      <div class="body">
        <SearchField
          :label="$t('meta.search')"
          :name="'sensor-search-input'"
          :placeholder="$t('costCenters.searchPlaceholder')"
          v-model="searchPhrase"
          @input="searchHandler"
        />
        <LoadingSpinner v-if="loading" />
        <div v-else-if="costCenters.length === 0" class="alert alert-no-entry">
          {{ $t('costCenters.noCostCenters') }}
        </div>
        <template v-else>
          <LinkList>
            <LinkListItem
              v-for="costCenter in costCenters"
              :key="costCenter.uuid"
              :primaryLink="`/company/cost_centers/${costCenter.uuid}`"
              :style="costCenter.archived ? 'background-color: #dadada' : ''"
            >
              <template #main-content>
                <span>
                  {{ costCenter.name }}
                  <template v-if="costCenter.archived">({{ $t('costCenters.archived') }})</template>
                </span>
              </template>
              <template #action-content>
                <Link :to="`/company/cost_centers/${costCenter.uuid}`" class="link-list-item__action">
                  <span class="material-icons">edit</span>
                </Link>

                <button
                  v-if="costCenter.archived"
                  @click="unarchiveCostCenter(costCenter.uuid)"
                  class="link-list-item__action"
                >
                  <span class="material-icons" :title="$t('costCenters.unarchive')">replay</span>
                </button>
                <button
                  v-else-if="costCenter.can_be_deleted"
                  @click="deleteCostCenter(costCenter.uuid)"
                  class="link-list-item__action"
                >
                  <span class="material-icons">delete</span>
                </button>
                <button
                  v-else
                  @click="archiveCostCenter(costCenter.uuid)"
                  class="link-list-item__action"
                  :title="$t('costCenters.archive')"
                >
                  <span class="material-icons">inventory</span>
                </button>
              </template>
            </LinkListItem>
          </LinkList>

          <pagination
            v-if="pagination"
            v-model="page"
            :records="pagination.total_objects"
            :per-page="pagination.per_page"
            :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
            @paginate="getCostCenters"
          />
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { debounce } from 'lodash'
import Pagination from 'vue-pagination-2'

import SearchField from '@/components/Form/Search/SearchField'
import LinkListItem from '@/components/LinkList/LinkListItem.vue'
import LinkList from '@/components/LinkList/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import CompanyNav from '@/components/company_nav'

import costCentersApi from '@/api/costCenters'
import { SEARCH_INPUT_DELAY } from '@/utils/searchInputDelay'

export default {
  name: 'cost_centers',
  inject: ['companyUser', 'showPopup'],
  components: {
    SectionHeader,
    CompanyNav,
    SearchField,
    LinkList,
    LinkListItem,
    LoadingSpinner,
    Pagination,
  },
  props: {
    searchInputDelay: {
      type: Number,
      default: SEARCH_INPUT_DELAY,
    },
  },
  data() {
    return {
      loading: true,
      pagination: null,
      page: 1,
      searchPhrase: '',
      costCenters: [],
    }
  },
  computed: {
    searchHandler() {
      return debounce(
        () => {
          // The resulting records of the search cause a new pagination
          // and we start with page 1 for showing the results.
          this.getCostCenters(1, false)
        },
        this.searchInputDelay, // Limit search API calls to once per 400ms while typing
        { trailing: true },
      )
    },
  },
  methods: {
    async getCostCenters(page = null, showLoadingSpinner = true) {
      if (showLoadingSpinner) this.loading = true
      if (page) this.page = page

      const params = {
        company_uuid: this.companyUser.company_uuid,
        page: this.page,
        search_phrase: this.searchPhrase || null, // nullifies the empty string
      }

      const response = await costCentersApi.getAll(params)

      this.costCenters = response.cost_centers
      this.pagination = response.pagination

      this.loading = false
    },
    async deleteCostCenter(costCenterUuid) {
      const confirmAlert = await this.showPopup({ content: this.$t('costCenters.deleteConfirmation'), type: 'confirm' })
      if (!confirmAlert) return null

      const response = await costCentersApi.delete(costCenterUuid)
      if (response.success) this.getCostCenters()
    },
    async archiveCostCenter(costCenterUuid) {
      const confirmAlert = await this.showPopup({
        content: this.$t('costCenters.archiveConfirmation'),
        type: 'confirm',
      })
      if (!confirmAlert) return null

      const response = await costCentersApi.archive(costCenterUuid)
      if (response.success) this.getCostCenters()
    },
    async unarchiveCostCenter(costCenterUuid) {
      const response = await costCentersApi.unarchive(costCenterUuid)
      if (response.success) this.getCostCenters()
    },
  },
  async mounted() {
    this.getCostCenters(1)
  },
}
</script>

<style lang="scss" scoped></style>
